import React from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  IconButton,
  Skeleton,
  TextField,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ThumbUpAlt from "@mui/icons-material/ThumbUpAlt";
import ThumbUpAltOutlined from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAlt from "@mui/icons-material/ThumbDownAlt";
import ThumbDownAltOutlined from "@mui/icons-material/ThumbDownAltOutlined";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import { tokens } from "../../theme";
import {
  useGetLatestBriefQuery,
  useGetFeedbackQuery,
  useSubmitLikeMutation,
  useSubmitCommentMutation,
  useDeleteLikeMutation,
} from "../../Services/revops-brief";
import {
  BriefItem,
  BriefFeedback,
  SubmitLikePayload,
  SubmitCommentPayload,
} from "../../Models/RevOpsBrief";

interface BriefItemRowProps {
  item: BriefItem;
  feedback: BriefFeedback | undefined;
  onLike: (payload: SubmitLikePayload) => void;
  onAddComment: (payload: SubmitCommentPayload) => void;
  onDeleteLike: (payload: { item_id: string; brief_date: string }) => void;
  isCommentLoading: boolean;
  briefDate: string;
  section: string;
}

function BriefItemRow({
  item,
  feedback,
  onLike,
  onAddComment,
  onDeleteLike,
  isCommentLoading,
  briefDate,
  section,
}: BriefItemRowProps) {
  const [showComposer, setShowComposer] = React.useState(false);
  const [commentText, setCommentText] = React.useState("");
  const [reactionModalOpen, setReactionModalOpen] = React.useState(false);
  const [pendingLiked, setPendingLiked] = React.useState<boolean | null>(null);
  const [reactionComment, setReactionComment] = React.useState("");

  const hasInteraction = !!item.id;
  const currentLike = item.id
    ? feedback?.likes.find((l) => l.item_id === item.id)
    : undefined;
  const isUp = currentLike?.liked === true;
  const isDown = currentLike?.liked === false;
  const itemComments = item.id
    ? (feedback?.comments.filter((c) => c.item_id === item.id) ?? [])
    : [];

  const handleSubmitComment = () => {
    if (!commentText.trim() || !item.id) return;
    onAddComment({
      item_id: item.id,
      content: commentText.trim(),
      brief_date: briefDate,
      section,
      item_text: item.text,
    });
    setCommentText("");
    setShowComposer(false);
  };

  const handleThumbClick = (liked: boolean) => {
    if (!item.id) return;
    const isActive = currentLike?.liked === liked;
    if(isActive) {
      onDeleteLike({ item_id: item.id, brief_date: briefDate });
      return;
    }
    setPendingLiked(liked);
    setReactionComment("");
    setReactionModalOpen(true);
  };
  const handleReactionSubmit = () => {
    if (!reactionComment.trim() || !item.id || pendingLiked === null) return;
    onLike({
      item_id: item.id,
      liked: pendingLiked,
      content: reactionComment.trim(),
      brief_date: briefDate,
      section,
      item_text: item.text,
    });
    setReactionModalOpen(false);
    setPendingLiked(null);
    setReactionComment("");
  };

  const handleReactionCancel = () => {
    setReactionModalOpen(false);
    setPendingLiked(null);
    setReactionComment("");
  };

  return (
    <Box
      sx={{
        py: 0.75,
        "&:hover .brief-feedback": hasInteraction ? { opacity: 1 } : {},
      }}
    >
      <Typography
        sx={{
          fontSize: "0.6rem",
          color: "#6B7280",
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          mb: 0.25,
        }}
      >
        {item.category}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
        <Typography
          sx={{ fontSize: "0.8rem", color: "#374151", lineHeight: 1.4, flex: 1 }}
        >
          {item.text}
        </Typography>

        {hasInteraction && (
          <Box
            className="brief-feedback"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              opacity: 0,
              transition: "opacity 0.15s",
              flexShrink: 0,
              pt: 0.1,
            }}
          >
            <IconButton
              size="small"
              onClick= {() => handleThumbClick(true)}
              sx={{ p: 0.25, color: isUp ? "#2563EB" : "#9CA3AF" }}
            >
            {isUp ? (
                <ThumbUpAlt sx={{ fontSize: "0.9rem" }} />
              ) : (
                <ThumbUpAltOutlined sx={{ fontSize: "0.9rem" }} />
              )}
            </IconButton>
    
            <IconButton
              size="small"
              onClick={() => handleThumbClick(false)}
              sx={{ p: 0.25, color: isDown ? "#DC2626" : "#9CA3AF" }}
            >
              {isDown ? (
                <ThumbDownAlt sx={{ fontSize: "0.9rem" }} />
              ) : (
                <ThumbDownAltOutlined sx={{ fontSize: "0.9rem" }} />
              )}
            </IconButton>

            <IconButton
              size="small"
              onClick={() => setShowComposer((v) => !v)}
              sx={{ p: 0.25, color: "#9CA3AF" }}
            >
              <Badge
                badgeContent={itemComments.length || null}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.55rem",
                    minWidth: 14,
                    height: 14,
                  },
                }}
              >
                <ChatBubbleOutline sx={{ fontSize: "0.9rem" }} />
              </Badge>
            </IconButton>
          </Box>
        )}
      </Box>

      {itemComments.length > 0 && (
        <Box sx={{ mt: 0.5, pl: 1, borderLeft: "2px solid #E5E7EB" }}>
          {itemComments.map((c, i) => (
            <Typography
              key={i}
              sx={{ fontSize: "0.7rem", color: "#6B7280", py: 0.2 }}
            >
              {c.content}
            </Typography>
          ))}
        </Box>
      )}

      {showComposer && (
        <Box sx={{ mt: 0.75, display: "flex", gap: 0.5, alignItems: "center" }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Add a note..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmitComment();
              }
            }}
            disabled={isCommentLoading}
            sx={{ "& .MuiInputBase-input": { fontSize: "0.75rem", py: 0.5 } }}
          />
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmitComment}
            disabled={!commentText.trim() || isCommentLoading}
            sx={{
              fontSize: "0.7rem",
              py: 0.4,
              px: 1.5,
              minWidth: 0,
              whiteSpace: "nowrap",
            }}
          >
            Save
          </Button>
        </Box>
      )}

      <Dialog open={reactionModalOpen} onClose={handleReactionCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "0.95rem" }}>Please leave a comment</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            placeholder="Add your comment..."
            value={reactionComment}
            onChange={(e) => setReactionComment(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReactionCancel}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReactionSubmit}
            disabled={!reactionComment.trim()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Section<T>({
  title,
  items,
  renderItem,
  emptyText = "None",
}: {
  title: string;
  items: T[];
  renderItem: (item: T, i: number) => React.ReactNode;
  emptyText?: string;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="overline"
        sx={{ fontSize: "0.65rem", color: "#6B7280", letterSpacing: "0.08em" }}
      >
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography sx={{ fontSize: "0.8rem", color: "#9CA3AF", mt: 0.5 }}>
          {emptyText}
        </Typography>
      ) : (
        items.map((item, i) => renderItem(item, i))
      )}
    </Box>
  );
}

export default function TodaysBrief() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data: brief, isLoading, isError } = useGetLatestBriefQuery();
  const { data: feedback } = useGetFeedbackQuery(brief?.date ?? "", {
    skip: !brief?.date,
  });
  const [submitLike] = useSubmitLikeMutation();
  const [submitComment, { isLoading: isCommentLoading }] =
    useSubmitCommentMutation();
  const [deleteLike] = useDeleteLikeMutation();

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        height={140}
        sx={{ mx: 2, mt: 2, borderRadius: 1 }}
      />
    );
  }

  if (isError || !brief) return null;

  const makeRow = (sectionKey: string) => (item: BriefItem, i: number) => (
    <BriefItemRow
      key={item.id ?? i}
      item={item}
      feedback={feedback}
      onLike={(payload) => submitLike(payload)}
      onDeleteLike={(payload) => deleteLike(payload)}
      onAddComment={(payload) => submitComment(payload)}
      isCommentLoading={isCommentLoading}
      briefDate={brief.date}
      section={sectionKey}
    />
  );

  return (
    <Card
      variant="outlined"
      sx={{ overflow: "hidden", p: 3, mt: 2, mx: 2, bgcolor: "#FFFFFF" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography
          variant="subtitle1"
          color={colors.gray[200]}
          bgcolor={colors.gray[900]}
          fontWeight="bold"
          sx={{ p: 1, px: 3, display: "inline-block" }}
        >
          Today's RevOps Brief
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "#9CA3AF" }}>
          {brief.date}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Section
          title="Key Changes"
          items={brief.what_changed ?? []}
          renderItem={makeRow("what_changed")}
        />
        <Section
          title="Risks"
          items={brief.risks_brief ?? []}
          renderItem={makeRow("risks_brief")}
        />
        <Section
          title="Opportunities"
          items={brief.opportunities_brief ?? []}
          renderItem={makeRow("opportunities_brief")}
        />
        <Section
          title="What to do next"
          items={brief.recommendations ?? []}
          renderItem={makeRow("recommendations")}
        />
      </Box>
    </Card>
  );
}